/**
 * TTN editor (2026-06-03).
 * Простий — поля перевізника, авто, водія, посилання на Реалізацію.
 * Без проводок.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tnn, type TnnCreate } from '@/api/tnn';
import { goodsSales } from '@/api/goodsSales';
import { orgs } from '@/api/catalogs';
import DatePopover from '@/components/DatePopover';
import { useConfirm } from '@/components/ConfirmProvider';


interface FormState extends Omit<TnnCreate, 'document_date'> {
  document_date: string;
}

const EMPTY: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  goods_sale_id: '',
  shipping_address: '',
  destination_address: '',
  carrier_name: '',
  carrier_edrpou: '',
  vehicle_plate: '',
  vehicle_model: '',
  driver_full_name: '',
  driver_license_number: '',
  cargo_description: '',
  total_places: null,
  total_weight_kg: null,
  comment: '',
};


export default function TnnEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });

  // Sales для dropdown (для нового — з URL ?sale_id=...).
  const salesQ = useQuery({
    queryKey: ['goods-sales-for-tnn'],
    queryFn: () => goodsSales.list({ posted: true, limit: 200 }),
  });

  const docQuery = useQuery({
    queryKey: ['tnn', id],
    queryFn: () => tnn.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isNew) {
      const firstOrg = orgList.data?.items[0]?.id ?? '';
      const urlSaleId = searchParams.get('sale_id') ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f,
        organization_id: firstOrg,
        goods_sale_id: urlSaleId || f.goods_sale_id,
      });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        goods_sale_id: d.goods_sale_id,
        shipping_address: d.shipping_address ?? '',
        destination_address: d.destination_address ?? '',
        carrier_name: d.carrier_name,
        carrier_edrpou: d.carrier_edrpou ?? '',
        vehicle_plate: d.vehicle_plate,
        vehicle_model: d.vehicle_model ?? '',
        driver_full_name: d.driver_full_name,
        driver_license_number: d.driver_license_number ?? '',
        cargo_description: d.cargo_description ?? '',
        total_places: d.total_places,
        total_weight_kg: d.total_weight_kg,
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data, orgList.data, searchParams]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  const create = useMutation({
    mutationFn: () => tnn.create({
      ...form,
      document_number: form.document_number || null,
      document_date: new Date(form.document_date).toISOString(),
      total_weight_kg: form.total_weight_kg ? String(form.total_weight_kg) : null,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['tnn-list'] });
      navigate(`/tnn/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => tnn.update(id!, {
      ...form,
      document_date: new Date(form.document_date).toISOString(),
      total_weight_kg: form.total_weight_kg ? String(form.total_weight_kg) : null,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tnn', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // Новий: create → post одним кліком.
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await tnn.create({
        ...form,
        document_number: form.document_number || null,
        document_date: new Date(form.document_date).toISOString(),
        total_weight_kg: form.total_weight_kg ? String(form.total_weight_kg) : null,
      });
      await tnn.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['tnn-list'] });
      navigate(`/tnn/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: update → post одним кліком.
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await tnn.update(id!, {
        ...form,
        document_date: new Date(form.document_date).toISOString(),
        total_weight_kg: form.total_weight_kg ? String(form.total_weight_kg) : null,
      });
      await tnn.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tnn', id] });
      qc.invalidateQueries({ queryKey: ['tnn-list'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => tnn.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tnn', id] });
      qc.invalidateQueries({ queryKey: ['tnn-list'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => tnn.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tnn-list'] });
      navigate('/tnn');
    },
  });

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова ТТН' : `ТТН: ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ проведено
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/tnn')}>Назад</button>
          {!isPosted && (
            <button className="primary"
                    onClick={() => isNew ? create.mutate() : update.mutate()}
                    disabled={create.isPending || update.isPending}>
              💾 Зберегти
            </button>
          )}
          {isNew && form.carrier_name && form.vehicle_plate && form.driver_full_name && (
            <button className="primary" onClick={() => createAndPost.mutate()}
                    disabled={create.isPending || createAndPost.isPending}
                    title="Зберігає та проводить документ">
              ▶ Провести
            </button>
          )}
          {!isNew && !isPosted && form.carrier_name && form.vehicle_plate && form.driver_full_name && (
            <button className="primary" onClick={() => updateAndPost.mutate()}
                    disabled={updateAndPost.isPending || update.isPending}
                    title="Зберігає поточні зміни та проводить документ">
              ▶ Провести
            </button>
          )}
          {!isNew && isPosted && (
            <button onClick={() => unpost.mutate()} disabled={unpost.isPending}>
              ◀ Розпровести
            </button>
          )}
          {!isNew && isPosted && (
            <a className="button" href={tnn.printUrl(id!)} target="_blank" rel="noopener noreferrer">
              🖨️ Друк
            </a>
          )}
          {!isNew && !isPosted && (
            <button className="danger"
                    onClick={() => confirm('Видалити ТТН?').then((ok) => { if (ok) remove.mutate(); })}>
              Видалити
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Організація</label>
          <select value={form.organization_id ?? ''}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew || isPosted}>
            <option value="">—</option>
            {orgList.data?.items.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>№ ТТН{' '}
            {isNew && <small style={{ color: 'var(--muted)' }}>(auto)</small>}
          </label>
          <input value={form.document_number ?? ''}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 disabled={isPosted}
                 placeholder={isNew ? 'ТТН-0001-2026 (auto)' : undefined} />
        </div>
        <div className="field">
          <label>Дата</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
      </div>

      <div className="field">
        <label>На підставі Реалізації <span style={{ color: 'red' }}>*</span></label>
        <select value={form.goods_sale_id}
                onChange={(e) => setForm({ ...form, goods_sale_id: e.target.value })}
                disabled={isPosted}>
          <option value="">— оберіть —</option>
          {salesQ.data?.items.map((s) => (
            <option key={s.id} value={s.id}>
              {s.document_number} від {new Date(s.document_date).toLocaleDateString('uk-UA')}
            </option>
          ))}
        </select>
      </div>

      <h3 style={{ marginTop: '1.25rem' }}>Адреси</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Адреса завантаження</label>
          <input value={form.shipping_address ?? ''}
                 onChange={(e) => setForm({ ...form, shipping_address: e.target.value })}
                 disabled={isPosted}
                 placeholder="Звідки везуть товар" />
        </div>
        <div className="field">
          <label>Адреса призначення</label>
          <input value={form.destination_address ?? ''}
                 onChange={(e) => setForm({ ...form, destination_address: e.target.value })}
                 disabled={isPosted}
                 placeholder="Куди доставити" />
        </div>
      </div>

      <h3 style={{ marginTop: '1.25rem' }}>Перевізник</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Найменування <span style={{ color: 'red' }}>*</span></label>
          <input value={form.carrier_name}
                 onChange={(e) => setForm({ ...form, carrier_name: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>ЄДРПОУ перевізника</label>
          <input value={form.carrier_edrpou ?? ''}
                 onChange={(e) => setForm({ ...form, carrier_edrpou: e.target.value })}
                 disabled={isPosted}
                 maxLength={10} />
        </div>
      </div>

      <h3 style={{ marginTop: '1.25rem' }}>Автомобіль і водій</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Реєстр. номер авто <span style={{ color: 'red' }}>*</span></label>
          <input value={form.vehicle_plate}
                 onChange={(e) => setForm({ ...form, vehicle_plate: e.target.value })}
                 disabled={isPosted}
                 placeholder="АА1234ВВ" />
        </div>
        <div className="field">
          <label>Марка / модель авто</label>
          <input value={form.vehicle_model ?? ''}
                 onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })}
                 disabled={isPosted} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>ПIБ водія <span style={{ color: 'red' }}>*</span></label>
          <input value={form.driver_full_name}
                 onChange={(e) => setForm({ ...form, driver_full_name: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>№ посвідчення</label>
          <input value={form.driver_license_number ?? ''}
                 onChange={(e) => setForm({ ...form, driver_license_number: e.target.value })}
                 disabled={isPosted} />
        </div>
      </div>

      <h3 style={{ marginTop: '1.25rem' }}>Вантаж</h3>
      <div className="field">
        <label>Опис вантажу</label>
        <input value={form.cargo_description ?? ''}
               onChange={(e) => setForm({ ...form, cargo_description: e.target.value })}
               disabled={isPosted}
               placeholder="Узагальнений опис (необов'язково)" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Кількість місць</label>
          <input type="number" min="0" value={form.total_places ?? ''}
                 onChange={(e) => setForm({ ...form, total_places: e.target.value ? Number(e.target.value) : null })}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>Вага брутто, кг</label>
          <input type="number" step="0.001" min="0" value={form.total_weight_kg ?? ''}
                 onChange={(e) => setForm({ ...form, total_weight_kg: e.target.value || null })}
                 disabled={isPosted} />
        </div>
      </div>

      <div className="field" style={{ marginTop: '0.75rem' }}>
        <label>Коментар</label>
        <input value={form.comment ?? ''}
               onChange={(e) => setForm({ ...form, comment: e.target.value })}
               disabled={isPosted} />
      </div>

      {error && <div className="error">{error}</div>}
    </div>
  );
}


function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string | Array<{ loc?: unknown[]; msg?: string }> } } }).response;
    const detail = r?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail.slice(0, 3).map((item) => {
        const field = Array.isArray(item.loc) && item.loc.length > 1
          ? String(item.loc[item.loc.length - 1]) : 'поле';
        return `${field}: ${item.msg ?? 'невідома помилка'}`;
      }).join('; ');
    }
    return 'Error';
  }
  return String(e);
}
